/**
 * EntranceExitLogs 도메인 모델
 * 입출입 로그 관련 데이터 구조 정의
 */

export interface EntranceExitLog {
  id: string;
  timestamp: string;
  user_id: string;
  user_name: string;
  action: 'entry' | 'enter' | 'exit' | 'outing';  // 'entry'와 'enter' 모두 허용, 'outing' 추가
  method: string;
  gate_id: string;
  gate_name: string;
  confidence?: number;
  outing_type?: string | null;  // 외출 유형 ('meal' | 'rest' | 'hospital' | 'academy' | 'other')
  notification_status?: 'success' | 'failed' | 'pending' | 'sending' | 'skipped' | `suppressed:${string}` | null;  // 알림톡 발송 상태
}

export interface CurrentInsideMember {
  user_id: string;
  name: string;
  entry_time: string;
}

export interface HourlyStats {
  entry: number;
  exit: number;
}

export interface EntranceExitStats {
  date: string;
  total_entries: number;
  total_exits: number;
  total_outings: number;  // 외출 횟수
  current_inside: number;
  current_inside_members: CurrentInsideMember[];
  hourly_stats: Record<string, HourlyStats>;
}

export type ActionFilter = 'all' | 'entry' | 'exit' | 'outing';

/**
 * 로그 필터 조건
 */
export interface LogFilter {
  startDate?: string;
  endDate?: string;
  userName?: string;
  action?: ActionFilter;
}

/**
 * 페이지네이션 정보
 */
export interface PaginationInfo {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

/**
 * 로그 조회 응답 (페이지네이션)
 */
export interface LogsResponse {
  success: boolean;
  data: {
    items: EntranceExitLog[];
    pagination: PaginationInfo;
  };
}

/**
 * Export 응답 (truncate 경고용)
 */
export interface ExportResponse {
  success: boolean;
  data: {
    logs: EntranceExitLog[];
    total: number;
    exported: number;
    is_truncated: boolean;
    max_limit: number;
  };
}

/**
 * 로그 필터 → URL 쿼리 파라미터 변환
 */
export const buildLogQueryParams = (filter: LogFilter): URLSearchParams => {
  const params = new URLSearchParams();
  if (filter.startDate) params.set('start_date', filter.startDate);
  if (filter.endDate) params.set('end_date', filter.endDate);
  if (filter.userName) params.set('user_name', filter.userName);
  if (filter.action && filter.action !== 'all') params.set('action', filter.action);
  return params;
};
